import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { DefaultButton } from "@/components/DefaultButton";
import AnimatedWaiting from "@/components/AnimatedWaiting";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Spinner } from "@/components/ui/spinner";
import { RevealOverlay } from "@/components/RevealOverlay";
import {
  queryClient,
  useClaimMission,
  useOneTimeMissionList,
  useStats,
} from "@/lib/hooks/useApi";
import type { Mission } from "@/lib/types/api";
import { usePrivy } from "@privy-io/react-auth";
import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { WithPullToRefresh } from "@/components/ui/pull-to-refresh";
import useUserAgent from "@/lib/hooks/useUserAgent";
import { getMissionRoute } from "./daily-mission";
import { Check } from "lucide-react";
import { WavyText } from "@/components/WavyText";
import { useTranslation } from "@/lib/hooks/useTranslation";
import { translateMissionDescription } from "@/lib/missionTranslations";

const FREEPASS_TICKET_IMG_URL =
  "https://cdn.navalabs.io/icons/freepass_j7fqhb.png";
const GACHA_COIN_IMG_URL =
  "https://cdn.navalabs.io/icons/gacha-icon_Gf5APY.png";
const LUCKY_DRAW_TICKET_IMG_URL =
  "https://cdn.navalabs.io/icons/ticket_33hdbL.png";

const getRewardIcon = (rewardType: string): string => {
  switch (rewardType) {
    case "free_pass":
      return FREEPASS_TICKET_IMG_URL;
    case "gacha_coin":
      return GACHA_COIN_IMG_URL;
    case "lucky_draw":
      return LUCKY_DRAW_TICKET_IMG_URL;
    case "trophies":
    default:
      return "https://cdn.navalabs.io/icons/trophy-icon_zozppj.png";
  }
};

const isInternalUrl = (url: string): boolean => {
  return url.startsWith("/");
};

export default function OneTimeMission() {
  const { user } = usePrivy();
  const [isMutating, setIsMutating] = useState(false);
  const navigate = useNavigate();
  const { isMobile } = useUserAgent();
  const { t } = useTranslation();
  const { data: statsData, refetch: refetchStats } = useStats(
    user?.wallet?.address ?? "",
  );
  const { data, isLoading, isError, refetch } = useOneTimeMissionList(
    statsData?.userId,
  );

  const [activeMissionId, setActiveMissionId] = useState<string | null>(null);
  const [clickedMissions, setClickedMissions] = useState<
    Record<string, boolean>
  >({});
  const [linkLoading, setLinkLoading] = useState<Record<string, boolean>>({});
  const [claimedMission, setClaimedMission] = useState<Mission | null>(null);
  const [isRevealOpen, setIsRevealOpen] = useState(false);
  const linkTimeoutsRef = useRef<Record<string, number | null>>({});

  const { mutateAsync: claimMission } = useClaimMission();

  const allMissions = useMemo(() => {
    return data?.pages.flatMap((page) => page.missions) || ([] as Mission[]);
  }, [data]);

  useEffect(() => {
    return () => {
      if (typeof window === "undefined") {
        return;
      }

      Object.values(linkTimeoutsRef.current).forEach((timeoutId) => {
        if (timeoutId) {
          window.clearTimeout(timeoutId);
        }
      });
    };
  }, []);

  const handleClaim = useCallback(
    async (missionId: string) => {
      const mission = allMissions.find((m) => m.id === missionId);
      if (!mission) return;

      setActiveMissionId(missionId);
      setIsMutating(true);

      try {
        await claimMission({ missionId });

        setClaimedMission(mission);
        setIsRevealOpen(true);

        // Refetch stats
        await refetchStats();

        // Invalidate and refetch mission queries
        await queryClient.invalidateQueries({
          queryKey: ["infiniteDailyMissions"],
        });
        await queryClient.invalidateQueries({
          queryKey: ["infiniteOneTimeMissions"],
        });
        await queryClient.invalidateQueries({ queryKey: ["stats"] });

        // Explicitly refetch the current mission list
        await refetch();
      } catch (error) {
        console.error("Error claiming:", error);
      } finally {
        setActiveMissionId(null);
        setIsMutating(false);
      }
    },
    [allMissions, claimMission, refetchStats, refetch],
  );

  if (isLoading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <Spinner />
      </div>
    );
  }

  if (isError) {
    return (
      <>
        <div className="flex items-center">{t("missions.error")}</div>
      </>
    );
  }

  if (allMissions.length === 0) {
    return (
      <>
        <div className="flex items-center">{t("missions.noMissions")}</div>
      </>
    );
  }

  const content = (
    <ScrollArea className="h-full px-2" type="auto">
      <div className="flex flex-col gap-2 pt-1">
        {allMissions.map((mission) => {
          const hasClicked = clickedMissions[mission.id];
          const isClaimed = mission.isClaimed;
          const isEligible = mission.isEligible;
          const isNew = mission.isNew;
          const route = getMissionRoute(mission.type);
          const url = mission.details.url?.trim();
          const hasUrl = Boolean(url);
          const isChecking = Boolean(linkLoading[mission.id]);

          const handleStart = () => {
            if (isChecking) {
              return;
            }

            if (hasUrl && url) {
              const isLinkMissionType =
                mission.type === "link-telegram" ||
                mission.type === "link-twitter" ||
                mission.type === "link-discord";

              const isInternal = isInternalUrl(url);

              if (isInternal) {
                // Internal URL: use navigate
                // For social link missions, add query parameter
                if (isLinkMissionType) {
                  navigate({
                    to: url,
                    search: { socialLink: true },
                  });
                } else {
                  navigate({ to: url });
                }

                // Only set hasClicked for non-link mission types
                if (!isLinkMissionType) {
                  setClickedMissions((prev) => ({
                    ...prev,
                    [mission.id]: true,
                  }));
                }
              } else {
                // External URL: open in new tab
                setLinkLoading((prev) => ({ ...prev, [mission.id]: true }));

                if (typeof window !== "undefined") {
                  window.open(url, "_blank", "noopener,noreferrer");

                  // Only set hasClicked for non-link mission types
                  // Link missions require isEligible from server
                  if (!isLinkMissionType) {
                    const timeoutId = window.setTimeout(() => {
                      setClickedMissions((prev) => ({
                        ...prev,
                        [mission.id]: true,
                      }));
                      setLinkLoading((prev) => {
                        const next = { ...prev };
                        delete next[mission.id];
                        return next;
                      });
                      linkTimeoutsRef.current[mission.id] = null;
                    }, 5000);

                    linkTimeoutsRef.current[mission.id] = timeoutId;
                  } else {
                    // For link missions, just clear loading state after opening
                    const timeoutId = window.setTimeout(() => {
                      setLinkLoading((prev) => {
                        const next = { ...prev };
                        delete next[mission.id];
                        return next;
                      });
                      linkTimeoutsRef.current[mission.id] = null;
                    }, 1000);

                    linkTimeoutsRef.current[mission.id] = timeoutId;
                  }
                } else {
                  // Only set hasClicked for non-link mission types
                  if (!isLinkMissionType) {
                    setClickedMissions((prev) => ({
                      ...prev,
                      [mission.id]: true,
                    }));
                  }
                  setLinkLoading((prev) => {
                    const next = { ...prev };
                    delete next[mission.id];
                    return next;
                  });
                }
              }
              return;
            }

            if (route) {
              navigate({ to: route });
            }
          };

          const hasClickedLink = hasUrl && hasClicked;
          // Link-based missions (telegram, twitter, discord) require isEligible from server
          // They cannot be claimed just by clicking the link
          const isLinkMissionType =
            mission.type === "link-telegram" ||
            mission.type === "link-twitter" ||
            mission.type === "link-discord";
          const canClaim = isLinkMissionType
            ? isEligible && !isChecking
            : (hasClickedLink || isEligible) && !isChecking;
          const shouldShowClaim = isLinkMissionType
            ? isEligible
            : hasClickedLink || isEligible;
          const isClaimDisabled =
            !canClaim || (activeMissionId === mission.id && isMutating);

          return (
            <div
              key={mission.id}
              className={
                "relative flex flex-col rounded-lg items-center p-3 blue-container"
              }
            >
              {isNew && (
                <div className="absolute -top-3 left-2">
                  <WavyText
                    text={t("missions.new")}
                    className="shine-sm text-xs"
                    amplitude={4}
                  />
                </div>
              )}
              <div className="flex justify-between w-full">
                <div className="flex flex-col gap-1">
                  <div className="flex flex-row gap-1 items-center">
                    <span className="text-white text-sm font-bold">
                      {translateMissionDescription(mission.description)}
                    </span>
                    {mission.details.threshold && (
                      <span className="text-xs text-zinc-400">
                        ({mission.progress}/{mission.details.threshold})
                      </span>
                    )}
                  </div>
                  <div className="flex items-center space-x-1">
                    <img
                      src={getRewardIcon(mission.rewardType)}
                      alt={t("missions.reward")}
                      className={`${mission.rewardType === "lucky_draw" ? "w-4 h-auto" : "h-4 w-4"}`}
                    />
                    <span className="text-white font-semibold text-sm">
                      +{mission.rewardAmount}
                    </span>
                  </div>
                </div>

                {isClaimed ? (
                  <DefaultButton
                    variant="green"
                    disabled
                    className={`h-8 px-3!`}
                  >
                    <span className="flex items-center gap-1.5 text-sm font-extrabold text-shadow-black -mt-1 uppercase">
                      <Check className="w-4 h-4" />
                      {t("missions.claimed")}
                    </span>
                  </DefaultButton>
                ) : isChecking ? (
                  <DefaultButton
                    variant="default"
                    disabled
                    className={`h-8 px-3!`}
                  >
                    <AnimatedWaiting
                      prefix={t("missions.checking")}
                      className="flex items-center gap-1.5 text-sm font-extrabold text-shadow-black -mt-1 uppercase"
                    />
                  </DefaultButton>
                ) : shouldShowClaim ? (
                  <DefaultButton
                    variant="green"
                    disabled={isClaimDisabled}
                    onClick={() => handleClaim(mission.id)}
                    className={`h-8 px-3!`}
                  >
                    {activeMissionId === mission.id && isMutating ? (
                      <Spinner className="w-4 h-4" />
                    ) : (
                      <span className="flex items-center gap-1.5 text-sm font-extrabold text-shadow-black -mt-1 uppercase">
                        {t("missions.claim")}
                      </span>
                    )}
                  </DefaultButton>
                ) : (
                  <DefaultButton
                    variant="default"
                    onClick={handleStart}
                    className={`h-8 px-3!`}
                  >
                    <span className="flex items-center gap-1.5 text-sm font-extrabold text-shadow-black -mt-1 uppercase">
                      {t("missions.start")}
                    </span>
                  </DefaultButton>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </ScrollArea>
  );

  return (
    <Fragment>
      <div className="flex-1 flex flex-col gap-1 min-h-0">
        {isMobile ? (
          <WithPullToRefresh
            refetch={refetch}
            queryKey={["infiniteOneTimeMissions", "completed"]}
            className="h-full flex flex-col"
          >
            {content}
          </WithPullToRefresh>
        ) : (
          content
        )}
      </div>
      <RevealOverlay
        isOpen={isRevealOpen && Boolean(claimedMission)}
        onClose={() => {
          setIsRevealOpen(false);
          setClaimedMission(null);
        }}
        title={t("missions.missionCompleted")}
        rewardText={t("missions.reward")}
        rewardValue={claimedMission?.rewardAmount ?? 0}
        buttonText={t("missions.close")}
        description={
          claimedMission
            ? t("missions.missionClaimed", {
                description: translateMissionDescription(
                  claimedMission.description,
                ),
              })
            : undefined
        }
        shouldUseRewardIcons={
          claimedMission?.rewardType !== "free_pass" &&
          claimedMission?.rewardType !== "gacha_coin" &&
          claimedMission?.rewardType !== "lucky_draw"
        }
        alternatePrizeImageSrc={
          claimedMission ? getRewardIcon(claimedMission.rewardType) : undefined
        }
        alternatePrizeImageSrcSize="h-24! w-auto"
        alternatePrizeText={
          claimedMission ? `+${claimedMission.rewardAmount}` : undefined
        }
      />
    </Fragment>
  );
}

export const Route = createFileRoute("/(game)/missions/onetime-mission")({
  component: OneTimeMission,
});
